import React, { FC } from 'react';

import { useMsal } from '@azure/msal-react';

import { useUrlQueryParam } from 'hooks/useUrlQueryParam';
import eventTracking from 'services/analytics/eventTracking';
import { t } from 'ttag';
import AuthButton from 'ui/AuthButton';

import * as navigationService from '../../lib/navigation';
import auth from '../../services/auth';
import { useRegistrationRedirectLink } from '../registration/hooks/useRegistrationRedirectLink';
import { useRegistrationSource } from '../registration/hooks/useRegistrationSource';
import { RegistrationMethod } from '../registration/hooks/useRegistrationSuccessEffect';
import { useVerifiedInviteTokenData } from '../registration/hooks/useVerifiedInviteTokenData';
import { msalLoginRequest } from './msalConfig';

import MSIcon from './images/microsoft.svg';

export interface MicrosoftAuthButtonProps {
    type?: 'standard' | 'icon';
    onLogin?: (
        nextUrl: string,
        is2FAEnabled?: boolean,
        method?: RegistrationMethod,
    ) => void;
    onRegister?: (
        nextUrl: string,
        email: string,
        registrationMethod?: RegistrationMethod,
    ) => void;
    errorHandler?: (error: { code: string; reason: string }) => void;
}

const MicrosoftAuthButton: FC<MicrosoftAuthButtonProps> = ({
    type = 'standard',
    onLogin = () => undefined,
    onRegister = () => undefined,
    errorHandler = () => undefined,
}) => {
    const [isPending, setIsPending] = React.useState<boolean>(false);
    const { instance } = useMsal();

    const referrer = useUrlQueryParam('ref') || '';
    const source = useRegistrationSource();
    const redirect = useRegistrationRedirectLink();

    const { email: invite_email } = useVerifiedInviteTokenData();

    const onAuth = async () => {
        try {
            const authResult = await instance.loginPopup(msalLoginRequest);
            setIsPending(true);
            const {
                nextUrl,
                is2FAEnabled,
                flow,
                email,
            } = await auth.microsoftAuth({
                access_token: authResult.accessToken,
                id_token: authResult.idToken,
                source,
                redirect,
                referrer,
                invite_email: invite_email || null,
            });

            if (flow === 'login') {
                onLogin(nextUrl, is2FAEnabled, 'microsoft');
                return;
            } else if (flow === 'registration') {
                eventTracking.sendToGTM({
                    event: 'funnel_reg_step_2_microsoft',
                    action: '',
                    category: '',
                });
                onRegister(nextUrl, email, 'microsoft');
                return;
            }

            navigationService.redirect(nextUrl);
        } catch (error) {
            errorHandler(error);
        } finally {
            setIsPending(false);
        }
    };

    return (
        <AuthButton
            icon={MSIcon}
            type={type}
            disabled={isPending}
            isPending={isPending}
            onClick={onAuth}
            pendingText="Microsoft"
        >{t`Вхід через Microsoft`}</AuthButton>
    );
};

export default MicrosoftAuthButton;
